import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Plus, Edit, Loader2, X } from "lucide-react";
import { getMessagesAuto, createMessageAuto, updateMessageAuto } from "@/lib/api";

const typeLabels = {
  accuse_reception: "Accusé de réception",
  confirmation: "Confirmation de réservation",
  rappel: "Rappel avant arrivée",
  remerciement: "Remerciement post-séjour"
};

export const MessagesCMS = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    type_message: "accuse_reception",
    sujet: "",
    contenu: ""
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const res = await getMessagesAuto();
      setMessages(res.data);
    } catch (error) {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await updateMessageAuto(editing.id, formData);
        toast.success("Message mis à jour !");
      } else {
        await createMessageAuto(formData);
        toast.success("Message créé !");
      }
      setShowModal(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const handleEdit = (msg) => {
    setEditing(msg);
    setFormData({
      type_message: msg.type_message,
      sujet: msg.sujet,
      contenu: msg.contenu
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditing(null);
    setFormData({ type_message: "accuse_reception", sujet: "", contenu: "" });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <Loader2 className="w-6 h-6 animate-spin text-[#004333]" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="btn-primary flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Nouveau modèle
        </button>
      </div>

      <div className="bg-[#FAFAF8] p-4 mb-4 text-sm text-gray-600">
        <strong>Variables disponibles :</strong> {"{{voyageur_nom}}"}, {"{{logement_titre}}"}, {"{{date_arrivee}}"}, {"{{date_depart}}"}, {"{{montant_total}}"}
      </div>

      {messages.length > 0 ? (
        <div className="space-y-4">
          {messages.map((msg) => (
            <div key={msg.id} className="bg-white border border-gray-200 p-6">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <span className="px-2 py-1 bg-[#004333]/10 text-[#004333] text-xs font-medium">
                    {typeLabels[msg.type_message] || msg.type_message}
                  </span>
                  <h4 className="font-semibold text-[#004333] mt-2">{msg.sujet}</h4>
                </div>
                <button onClick={() => handleEdit(msg)} className="p-2 text-gray-400 hover:text-[#004333]">
                  <Edit className="w-4 h-4" />
                </button>
              </div>
              <div className="text-sm text-gray-600 bg-gray-50 p-3 mt-2 max-h-24 overflow-y-auto">
                <div dangerouslySetInnerHTML={{ __html: msg.contenu.substring(0, 200) + "..." }} />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white border border-gray-200 p-12 text-center text-gray-500">
          Aucun modèle de message. Créez vos templates d'emails automatiques.
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="font-display text-xl text-[#004333]">
                {editing ? "Modifier le modèle" : "Nouveau modèle"}
              </h2>
              <button onClick={() => setShowModal(false)}>
                <X className="w-6 h-6 text-gray-400 hover:text-gray-600" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Type de message</label>
                <select
                  value={formData.type_message}
                  onChange={(e) => setFormData({ ...formData, type_message: e.target.value })}
                  className="input-hh"
                >
                  {Object.entries(typeLabels).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Sujet de l'email *</label>
                <input
                  type="text"
                  required
                  value={formData.sujet}
                  onChange={(e) => setFormData({ ...formData, sujet: e.target.value })}
                  className="input-hh"
                  placeholder="Ex: Votre réservation - {{logement_titre}}"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Contenu HTML *</label>
                <textarea
                  rows={12}
                  required
                  value={formData.contenu}
                  onChange={(e) => setFormData({ ...formData, contenu: e.target.value })}
                  className="input-hh font-mono text-sm"
                  placeholder="<h2>Bonjour {{voyageur_nom}},</h2>..."
                />
              </div>
              
              <div className="flex gap-4 pt-4">
                <button type="button" onClick={() => setShowModal(false)} className="btn-secondary flex-1">
                  Annuler
                </button>
                <button type="submit" className="btn-primary flex-1">
                  {editing ? "Mettre à jour" : "Créer"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MessagesCMS;
